
import { Vue, Component, Watch } from 'vue-property-decorator';
import ClientTopBar from '@/components/views/NewClientView/ClientTopBar.vue';
import EditClientTabs from '@/components/views/NewClientEdit/EditClientTabs.vue';
import Client from '@/models/Client';
import ClientViewSidebar from '@/components/global/sidebar/ClientViewSidebar.vue';
import ClientRepository from '@/repositories/ClientRepository';
import { NewFormData } from '@/interfaces/components/clients/NewFormData';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ClientAdditionalInformation } from '@/interfaces/components/clients/ClientAdditionalInformation';
import ProductGroup from '@/models/ProductGroup';
import ProductGroupRepository from '@/repositories/ProductGroupRepository';
import { UserRightsEnum } from '@/enums/global/UserRights';
import ContactConfig from '@/models/interfaces/Contact';
import ContactPerson from '@/models/ContactPerson';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';

@Component({
    name: 'NewEditClientView',
    components: { EditClientTabs, ClientViewSidebar, ClientTopBar },
})
export default class NewEditClientView extends Vue {
    private clientId: null | string = null;
    private client: any | null = null;
    private loadingOverlay = new LoadingOverlayHelper(this, {});

    private formData: NewFormData = { ...this.emptyFormData() };
    private initialLoading = true;
    private emptyFormData() {
        return {
            initialized: false,
            isCompany: false,
            name: null,
            clientAddresses: [],
            // Legacy address fields for backward compatibility
            addressForm: {
                id: null,
                streetNameAndNumber: null,
                poNumber: null,
                city: null,
                building: null,
                country: null,
                isDefault: true,
            },
            address: null,
            taxId: null,
            registerId: null,
            isTaxPayer: null,
            bankCode: null,
            currency: null,
            paymentType: null,
            priceType: null,
            priceListRegion: null,
            assignedUser: null,
            tags: [''],
            additionalNotes: '',
            contacts: [],
            discounts: [],
            contactPersons: [''],
            discount_ids: [''],
            clientTypes: [],
            sidebarData: {
                relatedFiles: [],
                customerHistory: null,
            },
            webpage: '',
            bigLogo: null,
            squareLogo: null,
            roltekAppPartner: false,
            backofficeUser: null,
            leadOrigin: null,
        };
    }

    private get userRights() {
        return this.$store.getters['jwtData/userRights'];
    }

    private get isItalyUG() {
        return this.$store.getters['jwtData/isItalyUG'];
    }

    private get canAccessLogistics() {
        return this.userRights.includes(UserRightsEnum.ACCESS_LOGISTICS);
    }

    private setDefaultContactAsFirst() {
        const contacts = this.client.contactPersons.slice();

        const defaultContactIndex = contacts.findIndex((contact: any) => contact.isDefault);

        if (defaultContactIndex < 0) {
            contacts[0].isDefault = true;
        } else {
            contacts.unshift(contacts.splice(defaultContactIndex, 1)[0]);
        }

        contacts.forEach((contact: any) => {
            if (contact.address) {
                contact.address.b2bDeliveryDays = this.parseB2bDeliveryDays(contact.address.b2bDeliveryDays);
            }
        });

        this.formData.contacts = contacts;
    }

    private serializeDataOnLoad() {
        this.formData = { ...this.emptyFormData() };

        if (this.client === null) {
            return;
        }

        this.setDefaultContactAsFirst();

        if (!this.client.isCompany) {
            this.formData.isCompany = false;
        } else {
            this.formData.isCompany = true;
            this.formData.bankCode = this.client.bankCode;
            this.formData.currency = this.client.currency_id;
            this.formData.paymentType = this.client.paymentType_id;
        }

        if (this.client.isCompany || this.isItalyUG) {
            this.formData.taxId = this.client.taxId;
            this.formData.registerId = this.client.registerId;
            this.formData.isTaxPayer = this.client.isTaxPayer;
        }

        this.formData.name = this.client.name;

        // Populate clientAddresses from client's addresses
        this.formData.clientAddresses = [];
        if (this.client?.addresses && this.client.addresses.length > 0) {
            this.formData.clientAddresses = this.client.addresses.map((addr: any) => {
                const deliveryDays = this.parseB2bDeliveryDays(addr.b2bDeliveryDays);
                return {
                    id: addr?.id,
                    streetNameAndNumber: addr?.streetNameAndNumber,
                    poNumber: addr?.poNumber,
                    city: addr?.city,
                    building: addr?.building,
                    country: addr?.country?.id ?? addr?.country,
                    addressType: addr?.addressType?.id,
                    isDefault: addr?.isDefault ?? false,
                    b2bDeliveryPhone: addr?.b2bDeliveryPhone,
                    b2bDeliveryEmail: addr?.b2bDeliveryEmail,
                    b2bDeliveryNotes: addr?.b2bDeliveryNotes,
                    b2bDeliveryDays: deliveryDays,
                };
            });
        } else if (this.client?.address) {
            // Legacy address fields for backward compatibility
            const deliveryDays = this.parseB2bDeliveryDays(this.client.address?.b2bDeliveryDays);
            this.formData.clientAddresses = [
                {
                    id: this.client?.address?.id,
                    streetNameAndNumber: this.client?.address?.streetNameAndNumber,
                    poNumber: this.client?.address?.poNumber,
                    city: this.client?.address?.city,
                    building: this.client?.address?.building,
                    country: this.client?.address?.country?.id,
                    isDefault: true,
                    b2bDeliveryPhone: this.client?.address?.b2bDeliveryPhone,
                    b2bDeliveryEmail: this.client?.address?.b2bDeliveryEmail,
                    b2bDeliveryNotes: this.client?.address?.b2bDeliveryNotes,
                    b2bDeliveryDays: deliveryDays,
                },
            ];
        }

        // Set the first address as default if none are marked as default to ensure there's always a default address
        const isOneAddressDefault = this.formData.clientAddresses.some((address) => address.isDefault);
        if (!isOneAddressDefault && this.formData.clientAddresses.length > 0) {
            this.formData.clientAddresses[0].isDefault = true;
        }

        this.formData.address = null;
        this.formData.priceType = this.client.priceType_id;
        this.formData.priceListRegion = this.client.priceListRegion_id;
        if (this.client.discounts.length > 0) {
            this.formData.discounts = this.client.discounts.map((discount: any) => {
                return {
                    id: discount.id,
                    name: discount.discountedEntity.name,
                    discountCategory: discount.discountType.toUpperCase(),
                    discountedEntityOid: discount.discountedEntityOid ? discount.discountedEntityOid : discount.oid,
                    productGroup: discount.productGroup_id,
                    isEditing: false,
                    isPercentage: discount.isPercentage,
                    pc1: discount.price1[0],
                    pc2: discount.price2[0],
                };
            });
        }

        this.formData.assignedUser = this.client?.assignedUser?.id ?? '';
        this.formData.additionalNotes = this.client?.additionalNotes ?? '';
        this.formData.tags = this.client.clientType_ids.slice();
        this.formData.webpage = this.client?.webpage ?? '';
        this.formData.bigLogo = this.client?.bigLogo;
        this.formData.squareLogo = this.client?.squareLogo;
        this.formData.roltekAppPartner = this.client?.roltekAppPartner;
        this.formData.backofficeUser = this.client?.backofficeUser?.id ?? '';
        this.formData.leadOrigin = this.client?.origin?.id ?? '';
        this.formData.initialized = true;
    }

    private parseB2bDeliveryDays(value: unknown): string[] {
        if (typeof value === 'string') {
            return value.split(' | ').map((day: string) => day.trim());
        }
        if (Array.isArray(value)) {
            return value;
        }
        return [];
    }

    private emptyAddress() {
        return {
            streetNameAndNumber: '',
            poNumber: '',
            city: '',
            building: '',
            country: undefined,
            addressType: '',
            b2bDeliveryPhone: '',
            b2bDeliveryEmail: '',
            b2bDeliveryNotes: '',
            b2bDeliveryDays: [],
        };
    }

    private get defaultClientAddress() {
        const selectedAddress =
            this.formData.clientAddresses?.find((address: any) => address.isDefault) || this.emptyAddress();

        return { ...selectedAddress, isDefault: false, addressType: selectedAddress.addressType || '' };
    }

    private addDefaultContactBasedOnCustomerData() {
        const contactData = {
            id: undefined,
            name: this.formData.name || undefined,
            email: '',
            phone: '',
            job: undefined,
            address: { ...this.defaultClientAddress },
            birthday: '',
            isDefault: true,
            default: true,
            useDefaultAddress: true,
        };

        this.formData.contacts.push(contactData as any);
    }

    private async fetchNecessaryConctactsDataAndSetDefaultValues() {
        if (!this.clientId) {
            return;
        }

        const client = ClientRepository.getById(this.clientId);

        // Wait for all contact data to be fetched before setting useDefaultAddress
        if (client?.contactPersons?.length) {
            await Promise.all(client.contactPersons.map((contact: ContactPerson) => ContactPerson.getById(contact.id)));
        }

        // If we have existing contacts with no address for some reason, we need to set it up
        // for proper functionality of the form
        await this.$nextTick();

        this.formData.contacts.forEach((contact: ContactConfig) => {
            if (!contact.address) {
                this.$set(contact, 'address', { ...this.emptyAddress() });
            }

            const matchedAddress = this.formData.clientAddresses?.find(
                (address: any) => contact.address && address.id === contact.address.id,
            );

            if (contact.address && matchedAddress) {
                this.$set(contact, 'useDefaultAddress', true);

                if (!contact.address?.addressType) {
                    this.$set(contact.address, 'addressType', matchedAddress?.addressType || '');
                }
            } else {
                this.$set(contact, 'useDefaultAddress', false);
            }
        });
    }

    private async fetchClient(isUpdating: boolean = false) {
        this.client = null;
        this.clientId = this.$route.path.includes('client/new') ? null : this.$route.params.clientId;

        if (this.clientId !== null) {
            const existingProductGroups = ProductGroupRepository.getAll();

            await Client.getById(this.clientId);

            if (existingProductGroups.length > 0 && isUpdating) {
                // Reset side effect of product groups being changed after updating discounts
                // This is needed to ensure that the product groups are up-to-date when the client is loaded
                ProductGroup.create({ data: existingProductGroups });
            }
        }

        this.client = ClientRepository.getById(this.clientId);
        this.serializeDataOnLoad();

        // Add default contact if no contacts exist
        if (!this.formData.contacts.length) {
            this.addDefaultContactBasedOnCustomerData();
        }

        // Fetch full contact data and set useDefaultAddress values
        await this.fetchNecessaryConctactsDataAndSetDefaultValues();

        await this.$nextTick();
        EventBus.$emit('discountsUpdated');

        return Promise.resolve();
    }

    private get isClientExisting() {
        return this.client !== null;
    }

    public get canEditClient() {
        return this.userRights.includes(UserRightsEnum.EDIT_CLIENT);
    }

    private get isSaveButtonDisabled() {
        return !this.canEditClient;
    }

    private updateClientData(data: ClientAdditionalInformation) {
        // loop through values and update the form data with the new values if there are fields to update
        for (const key in data.values) {
            if (data.fields.includes(key)) {
                switch (key) {
                    case 'city':
                        this.formData.addressForm!.city = data.values[key];
                        break;
                    case 'country':
                        this.formData.addressForm!.country = String(data.values[key]);
                        break;
                    case 'fullName':
                    case 'shortName':
                        this.formData.name = data.values[key];
                        break;
                    case 'postalCode':
                        this.formData.addressForm!.poNumber = data.values[key];
                        break;
                    case 'street':
                        this.formData.addressForm!.streetNameAndNumber = data.values[key];
                        break;
                    case 'registerID':
                        this.formData.registerId = data.values[key];
                        break;
                    case 'taxNumber':
                        this.formData.taxId = data.values[key];
                        break;
                    case 'taxPayer':
                        this.formData.isTaxPayer = data.values[key] ? true : false;
                        break;
                }
            }
        }
    }

    private created() {
        EventBus.$on(EventBusEvents.updateClientData, this.updateClientData);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.updateClientData, this.updateClientData);
    }

    private async mounted() {
        try {
            this.loadingOverlay.start();
            await this.fetchClient();
        } catch (error) {
        } finally {
            this.initialLoading = false;
            this.loadingOverlay.stop();
        }
    }

    @Watch('$route')
    private async onRouteChange() {
        await this.fetchClient();
    }

    @Watch('formData.isCompany', {
        immediate: false,
    })
    private onUserTypeChange(newVal: boolean, oldVal: boolean) {
        if (!this.formData.initialized) {
            return;
        }

        if (!newVal) {
            this.formData.webpage = null;
        }
    }
}
